import styles from './NewUser.module.css';

import { useState, useEffect } from 'react';

import Alert from '@mui/material/Alert';
import Button from "@mui/material/Button";
import FormControl from '@mui/material/FormControl';
import Icon from '@mui/material/Icon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import PersonAdd from '@mui/icons-material/PersonAdd';

import { addUser } from '../../api.js';
import { useAuth } from '../../AuthContext';

export const NewUser = ({ onNewUser }) => {
    const [ error, setError ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const [ isExpanded, setIsExpanded ] = useState(false);
    const [ requiredFields, setRequiredFields ] = useState([]);
    const [ newUser, setNewUser ] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        vendorName: ''
    });

    const { token } = useAuth();

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const validatePhoneNumber = (phoneNumber) => {
        if (!phoneNumber || phoneNumber.trim() === '') {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Phone number is required.');

            return false;
        }

        const phonePlusOne = /^\+1/.test(phoneNumber);
        const fullNumber = phonePlusOne ? phoneNumber : `+1${ phoneNumber }`;
        const isValid = /^(\+1)\d{10}$/.test(fullNumber);

        if (isValid) {
            setRequiredFields(prev => prev.filter(field => field !== 'phoneNumber'));
            setError(null);
            setNewUser(prev => ({ ...prev, phoneNumber: fullNumber }));

            return true;
        } else {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Invalid phone number format. Must be 10 digits.');

            return false;
        }
    };

    const handleOnChange = (e) => {
        const { name, value, required } = e.target;
        const fieldValue = value;

        setNewUser(prev => ({ ...prev, [ name ]: fieldValue }));

        if (name === 'phoneNumber') {
            const isValid = validatePhoneNumber(fieldValue);

            if (!isValid) {
                return;
            }
        }

        // Handle required field validation
        if (required && !fieldValue) {
            setRequiredFields(prev => [ ...prev.filter(field => field !== name), name ]);
            setError(`${ name } is required.`);

            return;
        }

        // Clear field from required list if now valid
        setRequiredFields(prev => prev.filter(field => field !== name));
        setError(null);
    };

    const handleOnNewUser = async () => {
        setLoading(true);
        setError(null);

        try {
            const ret = await addUser(newUser, token);

            if (ret && !ret.error) {
                onNewUser({ ...newUser, id: ret.id, displayName: `${ newUser.firstName } ${ newUser.lastName }`.trim() });

                setNewUser({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phoneNumber: '',
                    password: '',
                    vendorName: ''
                });

                setError(null);
                setIsExpanded(false);
            } else {
                setError(ret.error || 'Failed to create new user');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const missing = [];

        if (!newUser.firstName) missing.push('firstName');
        if (!newUser.lastName) missing.push('lastName');
        if (!newUser.phoneNumber) missing.push('phoneNumber');
        if (!newUser.email) missing.push('email');
        if (!newUser.password) missing.push('password');

        setRequiredFields(missing);
    }, [ newUser ]);

    return (
        <div className={ styles.container }>
            <div className={ styles.titleBar } onClick={ toggleExpanded }>
                <span>
                    <Icon component={ PersonAdd } sx={ { mr: 1 } } />
                    <Typography>Add New User</Typography>
                </span>

                <span>
                    <Icon component={ isExpanded ? ExpandLess : ExpandMore } />
                </span>
            </div>

            <div className={ `${ styles.content } ${ isExpanded ? styles.expanded : styles.collapsed }` }>
                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <FormControl>
                            <TextField
                                label="First Name"
                                variant="outlined"
                                size="small"
                                name='firstName'
                                autoComplete="off"
                                value={ newUser.firstName }
                                onChange={ handleOnChange }
                                required
                            />
                        </FormControl>
                    </div>

                    <div className={ styles.formField }>
                        <FormControl>
                            <TextField
                                label="Last Name"
                                variant="outlined"
                                size="small"
                                name='lastName'
                                autoComplete="off"
                                value={ newUser.lastName }
                                onChange={ handleOnChange }
                                required
                            />
                        </FormControl>
                    </div>

                    <div className={ styles.formField }>
                        <FormControl>
                            <TextField
                                label="Phone Number"
                                variant="outlined"
                                size="small"
                                name='phoneNumber'
                                slotProps={{ htmlInput: { maxLength: 12 } }}
                                autoComplete="off"
                                value={ newUser.phoneNumber }
                                onChange={ handleOnChange }
                                required
                            />
                        </FormControl>
                    </div>
                </div>

                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <FormControl>
                            <TextField
                                label="Vendor"
                                variant="outlined"
                                size="small"
                                name='vendorName'
                                autoComplete="off"
                                value={ newUser.vendorName }
                                onChange={ handleOnChange }
                            />
                        </FormControl>
                    </div>

                    <div className={ styles.formField }>
                        <FormControl>
                            <TextField
                                label="Email"
                                variant="outlined"
                                size="small"
                                name='email'
                                autoComplete="off"
                                value={ newUser.email }
                                onChange={ handleOnChange }
                                required
                            />
                        </FormControl>
                    </div>

                    <div className={ styles.formField }>
                        <FormControl>
                            <TextField
                                label="Password"
                                variant="outlined"
                                size="small"
                                name='password'
                                type='password'
                                autoComplete="new-password"
                                value={ newUser.password }
                                onChange={ handleOnChange }
                                required
                            />
                        </FormControl>
                    </div>
                </div>

                <div className={ styles.formRow }>
                    { error ?
                        <div className={ styles.formField }>
                            <Alert variant='filled' severity="error">{ error }</Alert>
                        </div>
                        :
                        <div className={ styles.formField }>
                            &nbsp;
                        </div>
                    }

                    <div className={ styles.formField }>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={ <PersonAdd /> }
                            onClick={ handleOnNewUser }
                            disabled={ loading || requiredFields.length > 0 }
                            loading={ loading }
                            loadingPosition='start'
                        >
                            { loading ? 'Creating...' : 'Add' }
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}